import Constants from '@/Constants';
import { LessonContentTO } from '@/models/LessonContentTO';
import { LessonData } from '@/models/LessonData';
import { LessonStartAndFinishData } from '@/models/LessonStartAndFinishData';
import { LinkMetadata } from '@/models/LinkMetadata';
import http from '@/services/HttpService';
import { toast } from 'react-toastify';
import { call, put } from 'redux-saga/effects';
import { PayloadAction } from 'typesafe-actions';
import { store } from '../index';
import { getCourseRequest } from '../list/action';
import {
    deleteLessonFailure,
    deleteLessonSuccess,
    finishLessonFailure,
    finishLessonSuccess,
    getLessonFailure,
    getLessonPathSuccess,
    getLessonsFailure,
    getLessonsSuccess,
    getLessonSuccess,
    postEvaluationFailure,
    postEvaluationSuccess,
    postLessonFailure,
    postLessonSuccess,
    putLessonFailure,
    putLessonSuccess,
    setLessonLinkMetadataFailure,
    setLessonLinkMetadataSuccess,
    startLessonFailure,
    startLessonSuccess,
} from './action';
import { LessonTypes } from './types';

export function* getLesson(
    action: PayloadAction<LessonTypes.GET_LESSON_REQUEST, { lessonId: number; refreshOnlyPath: boolean; isEvaluation: boolean }>,
) {
    try {
        let url;
        if (action.payload.isEvaluation) {
            url = Constants.FLOWLMS_LESSONS_EVALUATION_URI.replace('%lessonId', String(action.payload.lessonId));
        } else {
            url = `${Constants.LESSONS_URI}/${action.payload.lessonId}`;
        }

        const response = yield call(http.get, url);

        if (action.payload.refreshOnlyPath) {
            yield put(getLessonPathSuccess(response.data.path));
        } else {
            yield put(getLessonSuccess(response.data));
        }
    } catch (err) {
        yield put(getLessonFailure(err));
    }
}

export function* setLessonLinkMetadataRequest(action: PayloadAction<LessonTypes.SET_LESSON_EXTERNAL_LINK_METADATA_REQUEST, string>) {
    try {
        const [protocol, url] = action.payload.split('://');
        const path: string = Constants.TRACKING_METADATA_URI;
        const queryParams: string = `?protocol=${protocol}&url=${url}`;

        const response = yield call(http.get, path + queryParams);
        const linkMetadata: LinkMetadata = response.data;

        yield put(setLessonLinkMetadataSuccess(linkMetadata));
    } catch (error) {
        yield put(setLessonLinkMetadataFailure({ ...error, message: 'lessonLinkMetadataRequestFailure' }));
    }
}

export function* getLessons(action: PayloadAction<LessonTypes.GET_LESSONS_REQUEST, number>) {
    try {
        yield put(getCourseRequest(action.payload));

        const response = yield call(http.get, `${Constants.LMS_COURSES_URI}/${action.payload}/lessons`);
        yield put(getLessonsSuccess(response.data));
    } catch (err) {
        yield put(getLessonsFailure(err));
    }
}

export function* getLMSLessons(action: PayloadAction<LessonTypes.GET_LMS_LESSONS_REQUEST, number>) {
    try {
        yield put(getCourseRequest(action.payload));

        const response = yield call(http.get, `${Constants.FLOWLMS_COURSES_URI}/${action.payload}/lessons`);
        yield put(getLessonsSuccess(response.data));
    } catch (err) {
        yield put(getLessonsFailure(err));
    }
}

export function* postLesson(action: PayloadAction<LessonTypes.POST_LESSON_REQUEST, LessonData>) {
    const {
        identity: {
            data: {
                user: { orgId },
            },
        },
        locale: {
            data: { toastMessages },
        },
    } = store.getState();
    try {
        const { data } = yield call(http.post, Constants.LESSONS_URI, action.payload);
        yield put(postLessonSuccess(data));
        toast.success(toastMessages.LESSON_SAVE_SUCCESS);

        if (action.payload.mediaLesson && action.payload.mediaLesson.mediaType === 'VIDEO') {
            try {
                yield call(http.post, `${Constants.TRACKING_AWS}`, {
                    appId: Constants.APP_ID,
                    orgId,
                    entityType: 'LESSON',
                    fileKey: data.path,
                    entityId: data.id,
                    outputPath: `bagagem/assets/lesson/${data.identifier}/`,
                    locale: data.locale || data.content.locale,
                });
            } catch (err) {}
        }
    } catch (err) {
        toast.error(toastMessages.LESSON_SAVE_ERROR);
        yield put(postLessonFailure(err));
    }
}

export function* postEvaluation(action: PayloadAction<LessonTypes.POST_EVALUATION_REQUEST, LessonData>) {
    const {
        locale: {
            data: { toastMessages },
        },
    } = store.getState();
    try {
        const { data } = yield call(http.post, Constants.FLOWLMS_LESSONS_URI, action.payload);
        yield put(postEvaluationSuccess(data));
    } catch (err) {
        toast.error(toastMessages.LESSON_SAVE_ERROR);
        yield put(postEvaluationFailure(err));
    }
}

export function* putLesson(action: PayloadAction<LessonTypes.PUT_LESSON_REQUEST, LessonContentTO & { videoUpdate: boolean; hideToast: boolean }>) {
    const {
        identity: {
            data: {
                user: { orgId },
            },
        },
        locale: {
            data: { toastMessages },
        },
    } = store.getState();

    try {
        const { id, mediaType, path, identifier, locale, videoUpdate, hideToast } = action.payload;

        yield call(http.put, `${Constants.LESSONS_URI}/${id}`, action.payload);
        if (!hideToast) {
            toast.success(toastMessages.LESSON_SAVE_SUCCESS);
        }

        yield put(putLessonSuccess());
        if (mediaType === 'VIDEO' && videoUpdate === true) {
            try {
                yield call(http.post, `${Constants.TRACKING_AWS}`, {
                    appId: Constants.APP_ID,
                    orgId,
                    entityType: 'LESSON',
                    fileKey: path,
                    entityId: id,
                    outputPath: `bagagem/assets/lesson/${identifier}/`,
                    locale: locale,
                });
            } catch (err) {}
        }
    } catch (err) {
        toast.error(toastMessages.LESSON_SAVE_ERROR);
        yield put(putLessonFailure(err));
    }
}

export function* reorderLesson(action: PayloadAction<LessonTypes.PUT_LESSON_REORDER_REQUEST, { lessonId: number; order: number }>) {
    const {
        locale: {
            data: { toastMessages },
        },
    } = store.getState();

    try {
        const { lessonId, order } = action.payload;

        yield call(http.put, `${Constants.LESSONS_URI}/${lessonId}/reorder/${order}`);
    } catch (err) {
        toast.error(toastMessages.LESSON_SAVE_ERROR);
        yield put(putLessonFailure(err));
    }
}

export function* putEvaluationLesson(action: PayloadAction<LessonTypes.PUT_EVALUATION_LESSON_REQUEST, LessonData>) {
    const {
        identity: {
            data: {
                user: { orgId },
            },
        },
        locale: {
            data: { toastMessages },
        },
    } = store.getState();

    try {
        if (action.payload.evaluationLesson) {
            const url = Constants.FLOWLMS_LESSONS_EVALUATION_URI.replace('%lessonId', String(action.payload.evaluationLesson.id));
            yield call(http.put, url, action.payload);

            toast.success(toastMessages.LESSON_SAVE_SUCCESS);

            yield put(putLessonSuccess());
        } else {
            throw new Error();
        }
    } catch (err) {
        toast.error(toastMessages.LESSON_SAVE_ERROR);
        yield put(putLessonFailure(err));
    }
}

export function* deleteLesson(action: PayloadAction<LessonTypes.DELETE_LESSON_REQUEST, number>) {
    const {
        locale: {
            data: { toastMessages },
        },
    } = store.getState();
    try {
        yield call(http.delete, `${Constants.LESSONS_URI}/${action.payload}`);
        toast.success(toastMessages.LESSON_REMOVE_SUCCESS);
        yield put(deleteLessonSuccess(action.payload));
    } catch (err) {
        toast.error(toastMessages.LESSON_REMOVE_ERROR);
        yield put(deleteLessonFailure(err));
    }
}

export function* startLesson(action: PayloadAction<LessonTypes.START_LESSON_REQUEST, LessonStartAndFinishData>) {
    try {
        const { data } = yield call(http.post, Constants.START_LESSON_URI.replace('%s', String(action.payload.lessonId)), action.payload);
        yield put(startLessonSuccess(data.url));
    } catch (error) {
        yield put(startLessonFailure());
    }
}

export function* finishLesson(action: PayloadAction<LessonTypes.FINISH_LESSON_REQUEST, number>) {
    try {
        yield call(http.post, Constants.FINISH_LESSON_URI.replace('%s', action.payload.toString()));
        yield put(finishLessonSuccess());
    } catch (error) {
        yield put(finishLessonFailure());
    }
}
